import { useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import Tabs from '@components/ui/Tabs/Tabs';
import type { TabItem } from '@components/ui/Tabs/Tabs';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsCrossOrgSnapshotService } from '@services/dsCrossOrgSnapshotService';
import { getTenants } from '@services/tenantService';
import { dsTemplateService } from '@services/dsTemplateService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@stores/authStore';
import { useTableFilters } from '@hooks/useTableFilters';
import { useTranslation } from '@hooks/useTranslation';
import { useCountries } from '@hooks/useCountries';
import { getEmploymentTypes } from '@services/employmentTypeService';
import { getDocumentTypes } from '@services/documentTypeService';
import { toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import type { FilterFieldConfig } from '@/types/filters';
import type { DocumentSnapshot } from '@workpay/ds-shared/types/snapshot';
import { STATUS_CLASS_MAP, FORMAT_CLASS_MAP } from './snapshotAdminBadges';
import { useState } from 'react';
import styles from './SnapshotAdminList.module.scss';

type EnrichedAdminSnapshot = DocumentSnapshot & {
  countryName?: string;
  employmentTypeName?: string;
  documentTypeName?: string;
  tenantName?: string;
  masterTemplateName?: string;
};

interface SnapshotAdminListProps {
  searchParams: TableSearchParams;
}

export function SnapshotAdminList({ searchParams }: SnapshotAdminListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const addNotification = useNotificationStore((s) => s.addNotification);

  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam, tab } = searchParams;
  const page = urlPage ?? 0;
  const size = urlSize ?? 10;
  const activeTab = tab ?? 'IN_REVIEW';

  const sort: SortState | undefined = sortBy
    ? { columnId: sortBy, direction: (sortDir ?? 'desc') as 'asc' | 'desc' }
    : undefined;

  const statusTabs = useMemo<TabItem[]>(() => [
    { id: 'IN_REVIEW', label: t('K_SNAPSHOT_STATUS_IN_REVIEW', 'In Review') },
    { id: 'REVIEW_COMPLETED', label: t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete') },
    { id: 'ACTIVE', label: t('K_OPTION_ACTIVE', 'Active') },
    { id: 'INACTIVE', label: t('K_OPTION_INACTIVE', 'Inactive') },
    { id: 'ALL', label: t('K_STATUS_ALL', 'All') },
  ], [t]);

  // Reference data — fetched early so filter options can use them
  const { countries } = useCountries();

  const { data: employmentTypes } = useQuery({
    queryKey: ['employmentTypes', 'all'],
    queryFn: () => getEmploymentTypes({ size: 300 }),
  });

  const { data: documentTypes } = useQuery({
    queryKey: ['documentTypes', 'all'],
    queryFn: () => getDocumentTypes({ size: 300 }),
  });

  const { data: tenantsData } = useQuery({
    queryKey: ['tenants', 'all', { size: 300 }],
    queryFn: () => getTenants({ size: 300 }),
  });

  const { data: masterTemplates } = useQuery({
    queryKey: ['dsTemplates', 'all', { size: 300 }],
    queryFn: () => dsTemplateService.getAll({ size: 300 }),
  });

  // Filter out entries without an organization id — tenants in early onboarding
  // states may not have a Keycloak realm provisioned yet. Including them would
  // render filter <option>s with `value={undefined}` and produce duplicate
  // `key={null}` warnings. Use `tw.organization.id` (the auth-side org id the
  // backend search endpoint filters by), not the tenant record id.
  const tenantOptions = useMemo(
    () => (tenantsData?.content ?? [])
      .filter((tw) => !!tw.organization?.id)
      .map((tw) => ({ value: tw.organization.id, label: tw.tenant.name })),
    [tenantsData],
  );
  const masterOptions = useMemo(
    () => (masterTemplates?.content ?? [])
      .filter((m) => !!m.id)
      .map((m) => ({ value: m.id, label: m.name })),
    [masterTemplates],
  );
  const countryOptions = useMemo(
    () => countries.filter((c) => !!c.id).map((c) => ({ value: c.id, label: c.name })),
    [countries],
  );
  const empTypeOptions = useMemo(
    () => (employmentTypes?.content ?? [])
      .filter((e) => !!e.id)
      .map((e) => ({ value: e.id, label: e.name })),
    [employmentTypes],
  );
  const docTypeOptions = useMemo(
    () => (documentTypes?.content ?? [])
      .filter((d) => !!d.id)
      .map((d) => ({ value: d.id, label: d.name })),
    [documentTypes],
  );

  const filterFields = useMemo<FilterFieldConfig[]>(() => [
    {
      fieldKey: 'name',
      label: t('K_SNAPSHOT_COLUMN_NAME', 'Contract Name'),
      type: 'text',
      placeholder: t('K_SNAPSHOT_PLACEHOLDER_SEARCH', 'Search contract name...'),
    },
    {
      fieldKey: 'organizationId',
      label: t('K_COLUMN_TENANT', 'Client'),
      type: 'select',
      options: tenantOptions,
      placeholder: t('K_PLACEHOLDER_SELECT_TENANT', 'Select client...'),
    },
    {
      fieldKey: 'parentTemplateId',
      label: t('K_SNAPSHOT_ADMIN_SOURCE_TEMPLATE', 'Source Template'),
      type: 'select',
      options: masterOptions,
      placeholder: t('K_SNAPSHOT_ADMIN_SELECT_MASTER', 'Select master template...'),
    },
    {
      fieldKey: 'countryId',
      label: t('K_COLUMN_COUNTRY', 'Country'),
      type: 'select',
      options: countryOptions,
      placeholder: t('K_PLACEHOLDER_SELECT_COUNTRY', 'Select country...'),
    },
    {
      fieldKey: 'employmentTypeId',
      label: t('K_COLUMN_EMPLOYMENT_TYPE', 'Employment Type'),
      type: 'select',
      options: empTypeOptions,
      placeholder: t('K_PLACEHOLDER_SELECT_EMPLOYMENT_TYPE', 'Select employment type...'),
    },
    {
      fieldKey: 'documentTypeId',
      label: t('K_COLUMN_DOCUMENT_TYPE', 'Document Type'),
      type: 'select',
      options: docTypeOptions,
      placeholder: t('K_SNAPSHOT_ADMIN_SELECT_DOC_TYPE', 'Select document type...'),
    },
  ], [t, tenantOptions, masterOptions, countryOptions, empTypeOptions, docTypeOptions]);

  const {
    filters,
    filterPayload,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
  } = useTableFilters({
    routeTo: '/w/contracts',
    filtersParam,
    fields: filterFields,
  });

  const sortString = useMemo(() => {
    if (!sort) return 'createdOn,desc';
    return `${sort.columnId},${sort.direction}`;
  }, [sort]);

  const userFilters = (filterPayload?.filters ?? {}) as Record<string, unknown>;
  const statusForApi = activeTab === 'ALL' ? undefined : activeTab;

  // Merge status tab + user filters into the FilterRequest body.
  // Text search on "name" is encoded as filters.name.like by useTableFilters — the cross-org
  // backend's filter allowlist supports `name` (LIKE operator), so this works as-is.
  const apiFilters = useMemo(() => {
    const merged: Record<string, unknown> = { ...userFilters };
    if (statusForApi) merged.status = statusForApi;
    return Object.keys(merged).length > 0 ? merged : undefined;
  }, [userFilters, statusForApi]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['dsCrossOrgSnapshots', { page, size, sort: sortString, apiFilters }],
    queryFn: () => dsCrossOrgSnapshotService.search(
      { filters: apiFilters },
      { page, size, sort: sortString },
    ),
  });
  const listForbidden = isForbiddenError(error);

  const countryMap = useMemo(() => new Map(countries.map((c) => [c.id, c.name])), [countries]);
  const empTypeMap = useMemo(() => new Map((employmentTypes?.content ?? []).map((e) => [e.id, e.name])), [employmentTypes]);
  const docTypeMap = useMemo(() => new Map((documentTypes?.content ?? []).map((d) => [d.id, d.name])), [documentTypes]);
  const tenantMap = useMemo(() => {
    // Keyed by `organization.id` (the auth-side org id) to match `snap.organizationId`
    // on the snapshot rows. `tenant.organizationId` does not exist on the response.
    const map = new Map<string, string>();
    for (const tw of tenantsData?.content ?? []) {
      if (tw.organization?.id) map.set(tw.organization.id, tw.tenant.name);
    }
    return map;
  }, [tenantsData]);
  const masterMap = useMemo(() => new Map((masterTemplates?.content ?? []).map((m) => [m.id, m.name])), [masterTemplates]);

  const snapshots = useMemo<EnrichedAdminSnapshot[]>(() =>
    (data?.content ?? []).map((snap) => ({
      ...snap,
      countryName: snap.countryId ? countryMap.get(snap.countryId) : undefined,
      employmentTypeName: snap.employmentTypeId ? empTypeMap.get(snap.employmentTypeId) : undefined,
      documentTypeName: snap.documentTypeId ? docTypeMap.get(snap.documentTypeId) : undefined,
      tenantName: tenantMap.get(snap.organizationId),
      masterTemplateName: snap.parentTemplateId ? masterMap.get(snap.parentTemplateId) : undefined,
    })),
    [data, countryMap, empTypeMap, docTypeMap, tenantMap, masterMap],
  );
  const pagination = useMemo(() => toPaginationState(data), [data]);

  // Approve mutation
  const [approveTarget, setApproveTarget] = useState<EnrichedAdminSnapshot | null>(null);
  const approveMutation = useMutation({
    mutationFn: (id: string) => dsCrossOrgSnapshotService.approve(id),
    onSuccess: (_data, approvedId) => {
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshots'] });
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot', approvedId] });
      addNotification({ variant: 'success', title: t('K_DS_NOTIF_TEMPLATE_ACTIVATED', 'Contract approved successfully') });
      setApproveTarget(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const columns = useMemo<DataTableColumn<EnrichedAdminSnapshot>[]>(() => [
    {
      id: 'name',
      header: t('K_SNAPSHOT_COLUMN_NAME', 'Contract Name'),
      sortable: true,
      sortKey: 'name',
      width: '200px',
      cell: (item) => <span className={styles.cellPrimary} title={item.name}>{item.name}</span>,
    },
    {
      id: 'tenantName',
      header: t('K_COLUMN_TENANT', 'Client'),
      cell: (item) => item.tenantName || '\u2014',
    },
    {
      id: 'masterTemplateName',
      header: t('K_SNAPSHOT_ADMIN_SOURCE_TEMPLATE', 'Source Template'),
      cell: (item) => item.masterTemplateName || '\u2014',
    },
    {
      id: 'countryName',
      header: t('K_COLUMN_COUNTRY', 'Country'),
      cell: (item) => item.countryName || '\u2014',
    },
    {
      id: 'employmentTypeName',
      header: t('K_COLUMN_EMPLOYMENT_TYPE', 'Employment Type'),
      cell: (item) => item.employmentTypeName || '\u2014',
    },
    {
      id: 'templateFormat',
      header: t('K_DS_COLUMN_FORMAT', 'Format'),
      cell: (item) => (
        <span className={`${styles.badge} ${styles[FORMAT_CLASS_MAP[item.templateFormat] ?? 'badgeNeutral'] ?? ''}`}>
          {item.templateFormat}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('K_COLUMN_STATUS', 'Status'),
      cell: (item) => {
        const label =
          item.status === 'IN_REVIEW'
            ? t('K_SNAPSHOT_STATUS_IN_REVIEW', 'In Review')
            : item.status === 'REVIEW_COMPLETED'
              ? t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete')
              : toTitleCase(item.status);
        return (
          <span className={`${styles.badge} ${styles[STATUS_CLASS_MAP[item.status] ?? 'badgeNeutral'] ?? ''}`}>
            {label}
          </span>
        );
      },
    },
    {
      id: 'createdOn',
      header: t('K_COLUMN_CREATED', 'Created'),
      sortable: true,
      sortKey: 'createdOn',
      cell: (item) => new Date(item.createdOn).toLocaleDateString(),
    },
  ], [t]);

  const rowActions = useCallback((item: EnrichedAdminSnapshot) => {
    const actions: ProfileAction[] = [];

    actions.push({
      id: 'view',
      label: t('K_VIEW', 'View'),
      onClick: () => navigate({ to: '/w/contracts/$snapshotId', params: { snapshotId: item.id } }),
    });

    if (
      hasPermission('CONTRACT_TEMPLATES_APPROVE') &&
      (item.status === 'IN_REVIEW' || item.status === 'REVIEW_COMPLETED')
    ) {
      actions.push({
        id: 'approve',
        label: t('K_DS_ACTIVATE', 'Approve'),
        variant: item.status === 'REVIEW_COMPLETED' ? 'primary' : undefined,
        onClick: () => setApproveTarget(item),
      });
    }

    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, navigate, hasPermission]);

  const handleTabChange = useCallback((tabId: string) => {
    void navigate({
      to: '/w/contracts',
      search: { page: 0, size, tab: tabId },
      replace: true,
    });
  }, [navigate, size]);

  const handleSortChange = useCallback((newSort: SortState | undefined) => {
    void navigate({
      to: '/w/contracts',
      search: (prev: TableSearchParams) => ({
        ...prev,
        page: 0,
        sortBy: newSort?.columnId,
        sortDir: newSort?.direction,
      }),
      replace: true,
    });
  }, [navigate]);

  const handlePageChange = useCallback((newPage: number) => {
    void navigate({
      to: '/w/contracts',
      search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
      replace: true,
    });
  }, [navigate]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    void navigate({
      to: '/w/contracts',
      search: (prev: TableSearchParams) => ({ ...prev, page: 0, size: newSize }),
      replace: true,
    });
  }, [navigate]);

  const handleRowClick = useCallback((item: EnrichedAdminSnapshot) => {
    navigate({ to: '/w/contracts/$snapshotId', params: { snapshotId: item.id } });
  }, [navigate]);

  const getRowKey = (item: EnrichedAdminSnapshot) => item.id;

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <p>{t('K_SNAPSHOT_ADMIN_EMPTY_TITLE', 'No client contracts')}</p>
          <p>{t('K_SNAPSHOT_ADMIN_EMPTY_DESC', 'Client contract snapshots will appear here when master templates are activated.')}</p>
        </div>
      ),
    [listForbidden, t],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_CONTRACTS', 'Contracts')}</h1>
          <p className={styles.subtitle}>{t('K_SNAPSHOT_ADMIN_SUBTITLE', 'Manage client contract snapshots across all companies')}</p>
        </div>
      </div>

      <Tabs
        tabs={statusTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={snapshots}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        emptyState={emptyState}
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <AlertModal
        variant="warning"
        title={t('K_SNAPSHOT_ADMIN_APPROVE_TITLE', 'Approve Contract')}
        description={t('K_SNAPSHOT_ADMIN_APPROVE_DESC', 'Are you sure you want to approve "{name}"? This will make the contract active for the client.').replace('{name}', approveTarget?.name ?? '')}
        isOpen={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        primaryLabel={t('K_DS_ACTIVATE', 'Approve')}
        onPrimaryClick={() => { if (approveTarget) approveMutation.mutate(approveTarget.id); }}
        loading={approveMutation.isPending}
      />
    </div>
  );
}
